import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import React, { Fragment } from 'react';
import FormSelect from '@c/forms/controls/FormSelect';
import { useForm } from 'react-hook-form';
import { Bike, Make, RideType, UserBike } from 'models/shared';
import useBikes from '@util/hooks/useBikes';
import Button from '@ui/Button';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'context/AuthContext';
import { addUserBike, editUserBike } from '@util/firestore/users';
import { logError } from '@util/logError';
import { useToastContext } from 'context/ToastContext';

export default function MobileAddBikeModal({
  isOpen,
  onClose,
  bikeData,
  isEdit,
  index,
}: {
  isOpen: boolean;
  onClose: () => void;
  bikeData?: any;
  isEdit?: boolean;
  index?: any;
}) {
  const { control, setValue, handleSubmit, watch } = useForm<UserBike>({
    defaultValues: {
      ...(isEdit && bikeData?.year && { year: bikeData.year }),
      ...(isEdit && bikeData?.make && { make: bikeData.make }),
      ...(isEdit && bikeData?.model && { model: bikeData.model }),
      ...(isEdit && bikeData?.imageUrl && { imageUrl: bikeData.imageUrl }),
      ...(isEdit && bikeData?.type && { type: bikeData.type }),
    },
  });

  const { userDoc } = useAuth();
  const queryClient = useQueryClient();
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState('');
  const { showSuccessToast } = useToastContext();

  const form = watch();
  const { bikeYears, bikeMakes, bikeModels } = useBikes(
    [`${form.year ?? ''}`],
    [form.make ?? ''] as Make[],
    [form.type ?? ''] as ('Dirt Bikes' | 'ATV' | 'UTV' | 'Street')[]
  );

  const submit = async (form: UserBike) => {
    if (!userDoc) return;
    setSubmitting(true);

    try {
      if (isEdit && index !== undefined) {
        await editUserBike(userDoc, form, index);
        await queryClient.invalidateQueries({
          queryKey: ['authUser'],
        });
        onClose();
      } else {
        if (
          userDoc.bikes.findIndex(
            (b) =>
              b.model === form.model &&
              b.year === form.year &&
              b.make === form.make
          ) === -1
        ) {
          await addUserBike(userDoc.uid, {
            ...form,
            date_added: Date.now(),
          });
          await queryClient.invalidateQueries({
            queryKey: ['authUser'],
          });
          onClose();
        } else {
          setError('Bike already exists in your garage');
        }
      }
    } catch (e) {
      logError(e);
    }
    setSubmitting(false);
  };

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-50 overflow-hidden"
        onClose={() => {
          onClose();
          setValue('year', null);
          setValue('make', '' as Make);
          setValue('model', '');
          setValue('type', '' as RideType);
        }}
      >
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

          <div className="fixed inset-x-0 bottom-0 flex w-full">
            <TransitionChild
              as={Fragment}
              enter="transform transition ease-in-out duration-300 sm:duration-500"
              enterFrom="translate-y-full"
              enterTo="translate-y-0"
              leave="transform transition ease-in-out duration-300 sm:duration-500"
              leaveFrom="translate-y-0"
              leaveTo="translate-y-full"
            >
              <DialogPanel className="relative h-[60rem] w-full">
                <div className="flex h-full flex-col rounded-tl-2xl rounded-tr-2xl bg-white px-4 py-6 shadow-xl">
                  <div className="flex items-center justify-between px-4 sm:px-6">
                    <DialogTitle className="text-[2.2rem] font-medium text-gray-700">
                      {isEdit ? 'Edit Bike' : 'Add your vehicle'}
                    </DialogTitle>

                    <div className="ml-3 flex h-7 items-center">
                      <button
                        type="button"
                        className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                        onClick={onClose}
                      >
                        <span className="sr-only">Close panel</span>
                        <XMarkIcon
                          className="h-[3rem] w-[3rem]"
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                  </div>

                  <div className="relative mt-12 flex flex-col gap-y-8 overflow-visible px-4 text-black sm:px-6">
                    <div className="flex flex-col gap-y-2 border-b border-zinc-200 pb-8">
                      <h3 className={'text-[1.6rem] font-semibold'}>
                        Ride Type{' '}
                        <span className="text-brand-secondary">*</span>
                      </h3>

                      <FormSelect
                        allowEmpty={false}
                        control={control}
                        name="type"
                        placeholder="Select Type"
                        options={[
                          {
                            label: 'Dirt Bikes',
                            value: 'Dirt Bikes',
                            id: 'DIRT_BIKE',
                          },
                          {
                            label: 'ATV',
                            value: 'ATV',
                            id: 'ATV',
                          },
                          {
                            label: 'UTV',
                            value: 'UTV',
                            id: 'UTV',
                          },
                          {
                            label: 'Street',
                            value: 'Street',
                            id: 'STREET',
                          },
                        ]}
                      />
                    </div>

                    <FormSelect
                      allowEmpty={false}
                      control={control}
                      name="year"
                      placeholder="Select Year"
                      options={bikeYears.map((y) => ({
                        label: y.toString(),
                        value: y,
                        id: y.toString(),
                      }))}
                    ></FormSelect>
                    <FormSelect
                      allowEmpty={false}
                      control={control}
                      name="make"
                      placeholder="Select Make"
                      options={bikeMakes.sort().map((y) => ({
                        label: y,
                        value: y,
                        id: y,
                      }))}
                    ></FormSelect>
                    <FormSelect
                      allowEmpty={false}
                      control={control}
                      name="model"
                      disabled={
                        !form.year || !form.make || bikeModels.length === 0
                      }
                      placeholder={
                        form.year && form.make
                          ? bikeModels.length > 0
                            ? 'Select Model'
                            : 'No Models Found'
                          : 'Select Year and Make First'
                      }
                      options={bikeModels
                        .sort((a, b) => a.localeCompare(b))
                        .map((y) => ({
                          label: y,
                          value: y,
                          id: y,
                        }))}
                    ></FormSelect>
                  </div>

                  {error && (
                    <span className="text-sm text-red-500">{error}</span>
                  )}

                  <div className="mt-[7rem] flex w-full justify-center px-4">
                    <Button
                      text="Add to Garage"
                      type="secondary"
                      className={'w-full !rounded-2xl'}
                      onClick={() => {
                        handleSubmit(submit)();
                        showSuccessToast('Bike added to your garage');
                      }}
                      loading={submitting}
                      disabled={!form.year || !form.make || !form.model}
                    />
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
